import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Link, ExternalLink, CheckCircle, AlertCircle, CreditCard, Banknote } from 'lucide-react';

// Load Stripe (you'll need to add your publishable key)
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_51S13rxBnBT7CggSVggNOdJmGCBnUmZOdV3z5psTElNtVIBIJC1kJWsjbBwDxUG35yhjwAeR7bJk431J578QNABRQ008FawJFLT');

const StripeConnectSetup = () => {
  const { user, token } = useAuth();
  const [step, setStep] = useState('link'); // link, payment, authorization
  const [accountStatus, setAccountStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [onboardingUrl, setOnboardingUrl] = useState(null);

  useEffect(() => {
    checkAccountStatus();
  }, []);

  const checkAccountStatus = async () => {
    try {
      const response = await fetch('/stripe-connect/account-status', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAccountStatus(data.account);
        
        if (data.account && data.account.chargesEnabled && data.account.payoutsEnabled) {
          setStep('payment');
        }
      }
    } catch (error) {
      console.error('Error checking account status:', error);
    }
  };

  const linkStripeAccount = async () => {
    setLoading(true);
    setError(null);

    try {
      // First, try to get onboarding link (if account exists)
      let response = await fetch('/stripe-connect/onboarding-link', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      let data = await response.json();

      // If no account exists, create one first
      if (!response.ok && data.error === 'No Stripe Connect account found') {
        response = await fetch('/stripe-connect/create-account', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            country: 'GB',
            businessType: 'individual'
          })
        });

        data = await response.json();

        if (response.ok) {
          // Now get the onboarding link
          response = await fetch('/stripe-connect/onboarding-link', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          data = await response.json();
        }
      }

      if (response.ok) {
        // Redirect to Stripe onboarding
        window.location.href = data.accountLink || data.url;
      } else {
        setError(data.error || 'Failed to create onboarding link');
      }
    } catch (error) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSubmit = (e) => {
    e.preventDefault();
    linkStripeAccount();
  };

  const renderLinkAccount = () => (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-8">
      <div className="text-center mb-8">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
          <Link className="h-6 w-6 text-blue-600" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Link Your Stripe Account</h2>
        <p className="text-lg text-gray-600 mb-6">
          Connect your existing Stripe account to start receiving payments directly from other users.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
        <div className="flex items-start">
          <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-blue-800 mb-2">What you'll need:</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• An existing Stripe account (create one at <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className="underline">stripe.com</a>)</li>
              <li>• Your business information and bank account details</li>
              <li>• Government-issued ID for verification</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
          <div className="flex items-center">
            <CreditCard className="h-8 w-8 text-gray-400 mr-4" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">Stripe Connect</h3>
              <p className="text-sm text-gray-500">Link your existing Stripe account</p>
            </div>
          </div>
          <button
            onClick={linkStripeAccount}
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Linking...
              </>
            ) : (
              <>
                Link Account
                <ExternalLink className="h-4 w-4 ml-2" />
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="font-medium">Error</h4>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          Don't have a Stripe account?{' '}
          <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">
            Create one here
          </a>
        </p>
      </div>
    </div>
  );

  const renderPaymentSetup = () => (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Set Up Payment Method</h2>
      <p className="text-gray-600 mb-6">
        Your Stripe account is ready! Now let's set up a payment method for automatic invoice payments.
      </p>

      <PaymentMethodSetup onComplete={() => setStep('authorization')} />
    </div>
  );

  const renderAuthorization = () => (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment Authorization</h2>
      <p className="text-gray-600 mb-6">
        Give consent for automatic payment collection on your approved invoices.
      </p>

      <PaymentAuthorization onComplete={() => setStep('complete')} />
    </div>
  );

  const renderComplete = () => (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6 text-center">
      <div className="text-green-500 text-6xl mb-4">✅</div>
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Setup Complete!</h2>
      <p className="text-gray-600 mb-6">
        Your Stripe Connect account is ready. You can now receive payments directly from other users.
      </p>
      
      <button
        onClick={() => window.location.href = '/dashboard'}
        className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700"
      >
        Go to Dashboard
      </button>
    </div>
  );

  if (step === 'link') return renderLinkAccount();
  if (step === 'payment') return renderPaymentSetup();
  if (step === 'authorization') return renderAuthorization();
  if (step === 'complete') return renderComplete();

  return null;
};

// Payment Method Setup Component
const PaymentMethodSetup = ({ onComplete }) => {
  const { token } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!stripe || !elements) {
      setError('Stripe not loaded');
      setLoading(false);
      return;
    }

    try {
      // Get SetupIntent
      const response = await fetch('/stripe-connect/setup-payment-method', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          paymentMethodTypes: ['card', 'sepa_debit', 'bacs_debit']
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to setup payment method');
      }

      // Confirm setup intent
      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(
        data.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: 'Payment Method Setup'
            }
          }
        }
      );

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      // Save payment method
      const saveResponse = await fetch('/stripe-connect/save-payment-method', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          paymentMethodId: setupIntent.payment_method,
          paymentType: 'card',
          customerId: data.customerId
        })
      });

      if (saveResponse.ok) {
        onComplete();
      } else {
        const saveData = await saveResponse.json();
        throw new Error(saveData.error || 'Failed to save payment method');
      }

    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Details
        </label>
        <div className="border border-gray-300 rounded-md p-3">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
              },
            }}
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading || !stripe}
        className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Setting Up...' : 'Set Up Payment Method'}
      </button>

      {error && (
        <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}
    </form>
  );
};

// Payment Authorization Component
const PaymentAuthorization = ({ onComplete }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [maxAmount, setMaxAmount] = useState('1000');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Get user's payment methods
      const methodsResponse = await fetch('/stripe-connect/payment-methods', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const methodsData = await methodsResponse.json();

      if (!methodsResponse.ok) {
        throw new Error(methodsData.error || 'Failed to get payment methods');
      }

      if (!methodsData.paymentMethods.length) {
        throw new Error('No payment methods found');
      }

      const defaultMethod = methodsData.paymentMethods.find(pm => pm.isDefault);

      if (!defaultMethod) {
        throw new Error('No default payment method found');
      }

      // Create authorization
      const authResponse = await fetch('/stripe-connect/authorize-payment', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          paymentMethodId: defaultMethod.id,
          maxAmount: parseFloat(maxAmount),
          currency: 'GBP'
        })
      });

      if (authResponse.ok) {
        onComplete();
      } else {
        const authData = await authResponse.json();
        throw new Error(authData.error || 'Failed to create authorization');
      }

    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Maximum Payment Amount (GBP)
        </label>
        <input
          type="number"
          value={maxAmount}
          onChange={(e) => setMaxAmount(e.target.value)}
          min="1"
          step="0.01"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="1000.00"
        />
        <p className="text-sm text-gray-500 mt-1">
          This is the maximum amount that can be automatically charged to your payment method.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <h4 className="font-medium text-blue-900 mb-2">What you're authorizing:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Automatic payment collection on approved invoices</li>
          <li>• Payments up to £{maxAmount} per invoice</li>
          <li>• 7-day auto-approval for invoices you haven't manually approved</li>
          <li>• Secure processing through Stripe Connect</li>
        </ul>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Creating Authorization...' : 'Authorize Automatic Payments'}
      </button>

      {error && (
        <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}
    </form>
  );
};

// Wrapper component with Stripe Elements
const StripeConnectSetupWrapper = () => (
  <Elements stripe={stripePromise}>
    <StripeConnectSetup />
  </Elements>
);

export default StripeConnectSetupWrapper;
